import { Component, OnInit } from '@angular/core';
import { NavigationHistoryService } from '../core/navigation-history.service';
import { BaseComponentDirective } from '../base/base.component';
import { catchError, EMPTY, Subject, take, takeUntil, tap } from 'rxjs';
import { AuthService } from '../core/auth-service';
import { ConfirmationComponent } from '../confirmation/confirmation.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Buyer } from '../models/buyer.model';
import { BuyerService } from '../services/buyer.service';
import { QrComponent } from '../qr/qr.component';
import { Router } from '@angular/router';
import { RoleGuardService } from '../role-guard.service';
import { DiaryService } from '../diary/services/diary.service';
import { NotificationService } from '../services/notification.service';
import moment from 'moment';
import { AppointmentService } from '../services/appointment.service';
import { BuyerCalendarSchedule } from '../diary/models/buyer-calendar-schedule.model';
import { Site } from '../diary/models/site.model';
import { CheckInService } from '../checkIn.service';
import { LocationService } from '../services/location.service';
import { CheckInModel } from '../models/checkIn.model';
import { LookupService } from '../services/lookup.service';
import { ConfirmDialogService } from '../confirm-dialog/services/confirm-dialog.service';


@Component({
  selector: 'app-my-account',
  templateUrl: './my-account.component.html',
  styleUrl: './my-account.component.scss'
})
export class MyAccountComponent
  extends BaseComponentDirective
  implements OnInit {
  loading: boolean;
  userName: string;
  buyer: Buyer;
  currentPage: string;
  inTheGame: boolean;
  private readonly _destroying$ = new Subject<void>();
  userTaskCount = 0;
  authTaskCount = 0;
  isNotInBranch: boolean;
  dayId: number;
  todayId: number;
  buyerWeeklySchedule: BuyerCalendarSchedule;
  siteId: number;
  site: Site;
  canClock: boolean = false;
  canPlayGames: boolean = false;

  public constructor(
    private authService: AuthService,
    private modalService: NgbModal,
    private navigationHistory: NavigationHistoryService,
    private buyerService: BuyerService,
    private router: Router,
    private roleGuardService: RoleGuardService,
    private diaryService: DiaryService,
    private notifications: NotificationService,
    private appService: AppointmentService,
    private checkInService: CheckInService,
    private locationService: LocationService,
    private lookupService: LookupService,
    private confirmDialogService: ConfirmDialogService
  ) {
    super();
    this.setBuyer();
  }

  public async ngOnInit() {
    this.loading = true;
    await this.setBuyer();
    this.getIsNotInBranchValue();
    this.inTheGame = this.roleGuardService.hasRole(AuthService.viewTrophies);
    
    this.lookupService.getSettings$('FeatureToggle').pipe(
      tap(result => {
        const clockSetting = result.find(x => x.key === 'ClockInRegions');
        if (clockSetting) {
          this.canClock = this.hasRegionSetting(clockSetting.value);
        }
        const gameSetting = result.find(x => x.key === 'DailyGameRegions');
        if (gameSetting) {
          this.canPlayGames = this.hasRegionSetting(gameSetting.value);
        }
      })
    ).subscribe(() => { });

    this.loading = false;
  }

  private hasRegionSetting(settingValue: string): boolean {
    if (settingValue === 'all') {
      return true;
    } else {
      const regionId = this.buyer.regionId.toString();
      if (settingValue.split(',').some(id => regionId === id)) {
        return true;
      }
    }
    return false;
  }

  private async setBuyer() {
    this.buyer = await this.buyerService.getCurrentBuyer();
  }

  clearIds() {
    this.navigationHistory.setId(null);
  }

  logout() {
    this.clearIds();
    const modalRef = this.modalService.open(ConfirmationComponent, {
      keyboard: false,
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.title = 'Logout';
    modalRef.componentInstance.message = `Are you sure that you want to log out?`;
    modalRef.componentInstance.failButton = 'No';
    modalRef.componentInstance.successButton = 'Yes';

    modalRef.result.then(
      () => {
        this.authService.logout();
      },
      () => {
        this.modalService.dismissAll();
      }
    );
  }

  checkIn() {
    this.appService
      .getBuyerWeeklySchedule(moment(new Date()).format('YYYY-MM-DD'))
      .pipe(
        tap((result) => {
          this.buyerWeeklySchedule = result;
          this.check();
          const qr = Date.now();
          const qrData = `${window.location.origin}/validate?qr=${qr}&siteId=${this.siteId}&buyerName=${encodeURIComponent(this.buyer.employee.userName)}&branchName=${encodeURIComponent(this.site.name)}`;

          const modalRef = this.modalService.open(QrComponent, {
            keyboard: false,
            backdrop: 'static',
            centered: true,
            size: 'xl',
          });
          modalRef.componentInstance.isModal = true;
          modalRef.componentInstance.qrData = qrData;
          modalRef.result.then(
            () => { },
            () => { }
          );
        }),
        catchError(() => {
          return EMPTY;
        }),
        take(1)
      )
      .subscribe();
  }

  seeWhatIveWon() {
    this.router.navigate(['/pog']);
  }

  playTheGame() {
    this.router.navigate(['/play']);
  }

  getIsNotInBranchValue() {
    this.diaryService
      .getIsNotInBranchValue()
      .pipe(
        takeUntil(this._destroying$),
        catchError((err) => {
          this.notifications.dangerToast(
            'Unable to retrieve is in branch value',
            err
          );
          return EMPTY;
        })
      )
      .subscribe((result) => {
        this.isNotInBranch = result;
      });
  }

  check() {
    this.dayId = moment(Date.now()).day();
    this.todayId = moment(Date.now()).day();
    const intervals = this.buyerWeeklySchedule.buyerWorkingIntervals.filter((x) => x.dayId === this.dayId);
    if (this.todayId === this.dayId) {
      this.siteId = this.appService.getSiteId(this.buyerWeeklySchedule, this.dayId);
    } else {
      this.siteId = this.appService.getSiteIdBasedOnStartTime(intervals, this.dayId, this.todayId)
    }
    this.site = this.buyerWeeklySchedule.sites.find((x) => x.siteId === this.siteId);
  }

  clockAction() {
    this.appService.getBuyerWeeklySchedule(moment(new Date()).format('YYYY-MM-DD')).pipe(
      tap((result) => {
        this.buyerWeeklySchedule = result;
        this.check();
        this.locationService.createPositionStream().pipe(take(1)).subscribe(pos => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const checkInModel = new CheckInModel();
          checkInModel.branchName = this.site.name;
          checkInModel.buyerName = this.buyer.employee.userName;
          checkInModel.location = JSON.stringify({ latitude: lat, longitude: lng });
          checkInModel.siteId = this.siteId;
          checkInModel.latitude = lat;
          checkInModel.longitude = lng;
          checkInModel.selfCheckIn = true;
          this.checkInService.sendRMCheckInMessage$(checkInModel).subscribe();
          this.confirmDialogService.successMessage('Clock in/out successful');
        });
      }),
      catchError(() => {
        return EMPTY;
      }),
      take(1)
    ).subscribe();
  }
}
